import {
  ECurrencySymbolDisplay,
  EFontFamily,
  EFontSize,
  EFooterAlignment,
  EMarginSize,
  EPageOrientation,
  EShowPageSetting
} from '../types';
import {
  SET_FIRST_RENDER,
  TOGGLE_HEADER,
  TOGGLE_SHOW_APPLIED_FILTERS,
  TOGGLE_SHOW_EXPORT_TIMESTAMP,
  TOGGLE_SHOW_FOOTER,
  TOGGLE_SHOW_GRIDLINES,
  TOGGLE_SHOW_NOTE,
  TOGGLE_SHOW_NUMBER_FORMATTING_THOUSANDS,
  TOGGLE_SHOW_PAGE_NUMBER,
  TOGGLE_SHOW_PRIMARY_TITLE,
  TOGGLE_SHOW_SECONDARY_TITLE,
  TOGGLE_SHOW_SUBTITLE,
  UPDATE_COLUMNS_PER_PAGE,
  UPDATE_FONT_FAMILY,
  UPDATE_FONT_SIZE,
  UPDATE_MARGIN_SIZE,
  UPDATE_MAX_COLUMN_WIDTH,
  UPDATE_NOTE,
  UPDATE_NOTE_ALIGNMENT,
  UPDATE_PAGE_NUMBER_ALIGNMENT,
  UPDATE_PAGE_ORIENTATION,
  UPDATE_TIMESTAMP_ALIGNMENT,
  UPDATE_PREVIEW_COUNT,
  UPDATE_PREVIEW_LINK,
  UPDATE_PREVIEW_TIMER_ID,
  UPDATE_PRIMARY_TITLE,
  UPDATE_SECONDARY_TITLE,
  UPDATE_SHOW_PAGE_SETTING,
  UPDATE_SHOW_PDF_LOADING_SPINNER,
  UPDATE_SUBTITLE,
  UPDATE_CURRENCY_SYMBOL_DISPLAY,
  TOGGLE_SHOW_UNDERLINES,
  TOGGLE_KEEP_ROW_GROUPS_TOGETHER_ON_PAGE_BREAK,
  TOGGLE_KEEP_COLUMN_GROUPS_TOGETHER_ON_PAGE_BREAK
} from './actions';

export interface FormState {
  pageOrientation: EPageOrientation;
  marginSize: EMarginSize;
  fontFamily: EFontFamily;
  showHeader: boolean;
  showPageSetting: EShowPageSetting;
  primaryTitle: string;
  secondaryTitle: string;
  subtitle: string;
  showPrimaryTitle: boolean;
  showSecondaryTitle: boolean;
  showSubtitle: boolean;
  showNumberFormattingThousands: boolean;
  showAppliedFilters: boolean;
  showPdfLoadingSpinner: boolean;
  previewCount: number;
  previewLink: string;
  firstRender: boolean;
  previewTimerId: number;
  showFooter: boolean;
  note: string;
  showNote: boolean;
  showPageNumber: boolean;
  showExportTimeStamp: boolean;
  summaryTableFontSize: EFontSize;
  columnsPerPage: number;
  showGridlines: boolean;
  maxColumnWidth: number;
  pageNumberAlignment: EFooterAlignment;
  noteAlignment: EFooterAlignment;
  timestampAlignment: EFooterAlignment;
  currencySymbolDisplay: ECurrencySymbolDisplay;
  showUnderlines: boolean;
  keepRowGroupsTogetherOnPageBreaks: boolean;
  keepColumnGroupsTogetherOnPageBreaks: boolean;
}

export const initForm: FormState = {
  pageOrientation: EPageOrientation.portrait,
  marginSize: EMarginSize.normal,
  fontFamily: EFontFamily.dmSans,
  showHeader: true,
  showPageSetting: EShowPageSetting.onAllPages,
  primaryTitle: '',
  secondaryTitle: '',
  subtitle: '',
  showPrimaryTitle: true,
  showSecondaryTitle: true,
  showSubtitle: true,
  showNumberFormattingThousands: false,
  showAppliedFilters: false,
  showPdfLoadingSpinner: false,
  previewCount: 0,
  previewLink: '',
  firstRender: true,
  previewTimerId: 0,
  showFooter: true,
  note: '',
  showNote: false,
  showPageNumber: true,
  showExportTimeStamp: true,
  summaryTableFontSize: EFontSize.normal,
  columnsPerPage: 8,
  showGridlines: true,
  maxColumnWidth: 150,
  pageNumberAlignment: EFooterAlignment.right,
  noteAlignment: EFooterAlignment.center,
  timestampAlignment: EFooterAlignment.left,
  currencySymbolDisplay: ECurrencySymbolDisplay.All,
  showUnderlines: false,
  keepRowGroupsTogetherOnPageBreaks: false,
  keepColumnGroupsTogetherOnPageBreaks: false
};

type FormPayload = FormState[keyof FormState];
type ReducerHandler = (state: FormState, payload: FormPayload) => FormState;

const toggleHandlers: Record<string, (state: FormState) => FormState> = {
  [TOGGLE_HEADER]: (state) => ({ ...state, showHeader: !state.showHeader }),
  [TOGGLE_SHOW_PRIMARY_TITLE]: (state) => ({ ...state, showPrimaryTitle: !state.showPrimaryTitle }),
  [TOGGLE_SHOW_SECONDARY_TITLE]: (state) => ({ ...state, showSecondaryTitle: !state.showSecondaryTitle }),
  [TOGGLE_SHOW_SUBTITLE]: (state) => ({ ...state, showSubtitle: !state.showSubtitle }),
  [TOGGLE_SHOW_NUMBER_FORMATTING_THOUSANDS]: (state) => ({
    ...state,
    showNumberFormattingThousands: !state.showNumberFormattingThousands
  }),
  [TOGGLE_SHOW_APPLIED_FILTERS]: (state) => ({ ...state, showAppliedFilters: !state.showAppliedFilters }),
  [TOGGLE_SHOW_FOOTER]: (state) => ({ ...state, showFooter: !state.showFooter }),
  [TOGGLE_SHOW_NOTE]: (state) => ({ ...state, showNote: !state.showNote }),
  [TOGGLE_SHOW_PAGE_NUMBER]: (state) => ({ ...state, showPageNumber: !state.showPageNumber }),
  [TOGGLE_SHOW_EXPORT_TIMESTAMP]: (state) => ({ ...state, showExportTimeStamp: !state.showExportTimeStamp }),
  [TOGGLE_SHOW_GRIDLINES]: (state) => ({ ...state, showGridlines: !state.showGridlines }),
  [TOGGLE_SHOW_UNDERLINES]: (state) => ({ ...state, showUnderlines: !state.showUnderlines }),
  [TOGGLE_KEEP_ROW_GROUPS_TOGETHER_ON_PAGE_BREAK]: (state) => ({
    ...state,
    keepRowGroupsTogetherOnPageBreaks: !state.keepRowGroupsTogetherOnPageBreaks
  }),
  [TOGGLE_KEEP_COLUMN_GROUPS_TOGETHER_ON_PAGE_BREAK]: (state) => ({
    ...state,
    keepColumnGroupsTogetherOnPageBreaks: !state.keepColumnGroupsTogetherOnPageBreaks
  })
};

const payloadHandlers: Record<string, ReducerHandler> = {
  [UPDATE_PAGE_ORIENTATION]: (state, payload) => ({ ...state, pageOrientation: payload as EPageOrientation }),
  [UPDATE_MARGIN_SIZE]: (state, payload) => ({ ...state, marginSize: payload as EMarginSize }),
  [UPDATE_SHOW_PAGE_SETTING]: (state, payload) => ({ ...state, showPageSetting: payload as EShowPageSetting }),
  [UPDATE_PRIMARY_TITLE]: (state, payload) => ({ ...state, primaryTitle: payload as string }),
  [UPDATE_SECONDARY_TITLE]: (state, payload) => ({ ...state, secondaryTitle: payload as string }),
  [UPDATE_SUBTITLE]: (state, payload) => ({ ...state, subtitle: payload as string }),
  [UPDATE_SHOW_PDF_LOADING_SPINNER]: (state, payload) => ({ ...state, showPdfLoadingSpinner: payload as boolean }),
  [UPDATE_PREVIEW_COUNT]: (state, payload) => ({ ...state, previewCount: payload as number }),
  [UPDATE_PREVIEW_LINK]: (state, payload) => ({ ...state, previewLink: payload as string }),
  [SET_FIRST_RENDER]: (state, payload) => ({ ...state, firstRender: payload as boolean }),
  [UPDATE_PREVIEW_TIMER_ID]: (state, payload) => ({ ...state, previewTimerId: payload as number }),
  [UPDATE_NOTE]: (state, payload) => ({ ...state, note: payload as string }),
  [UPDATE_FONT_FAMILY]: (state, payload) => ({ ...state, fontFamily: payload as EFontFamily }),
  [UPDATE_FONT_SIZE]: (state, payload) => ({ ...state, summaryTableFontSize: payload as EFontSize }),
  [UPDATE_COLUMNS_PER_PAGE]: (state, payload) => ({ ...state, columnsPerPage: payload as number }),
  [UPDATE_MAX_COLUMN_WIDTH]: (state, payload) => ({ ...state, maxColumnWidth: payload as number }),
  [UPDATE_PAGE_NUMBER_ALIGNMENT]: (state, payload) => ({ ...state, pageNumberAlignment: payload as EFooterAlignment }),
  [UPDATE_NOTE_ALIGNMENT]: (state, payload) => ({ ...state, noteAlignment: payload as EFooterAlignment }),
  [UPDATE_TIMESTAMP_ALIGNMENT]: (state, payload) => ({ ...state, timestampAlignment: payload as EFooterAlignment }),
  [UPDATE_CURRENCY_SYMBOL_DISPLAY]: (state, payload) => ({
    ...state,
    currencySymbolDisplay: payload as ECurrencySymbolDisplay
  })
};

type ToggleMap = { [key: string]: ((state: FormState) => FormState) | undefined };
type PayloadMap = { [key: string]: ReducerHandler | undefined };

export const APPLY_ENTITY_CONFIG = 'APPLY_ENTITY_CONFIG';

export const formReducer = (state: FormState, action: { type: string; payload?: FormPayload }): FormState => {
  if (action.type === APPLY_ENTITY_CONFIG) {
    return { ...state, ...(action.payload as unknown as Partial<FormState>) };
  }

  const toggleHandler = (toggleHandlers as ToggleMap)[action.type];
  if (toggleHandler) return toggleHandler(state);

  const payloadHandler = (payloadHandlers as PayloadMap)[action.type];
  if (payloadHandler && action.payload !== undefined) return payloadHandler(state, action.payload);

  throw new Error('Unsupported action type');
};
